import { useEffect, useState } from "react";
import { fetchDealershipsByDN } from "../api/fetchDealerships";

const dealershipCityCache = new Map<string, string>();
const dealershipCityPending = new Map<string, Promise<string>>();

export const getDealershipCityByDnCached = async (dn: string): Promise<string> => {
  const normalizedDn = dn.trim();

  if (!normalizedDn) return "";

  const cachedCity = dealershipCityCache.get(normalizedDn);
  if (cachedCity) return cachedCity;

  const pendingRequest = dealershipCityPending.get(normalizedDn);
  if (pendingRequest) return pendingRequest;

  const request = fetchDealershipsByDN(normalizedDn)
    .then((dealership) => {
      const city = dealership?.city?.trim() ?? "";

      if (city) {
        dealershipCityCache.set(normalizedDn, city);
      }

      return city;
    })
    .catch(() => "")
    .finally(() => {
      dealershipCityPending.delete(normalizedDn);
    });

  dealershipCityPending.set(normalizedDn, request);

  return request;
};

export const useDealershipCityByDn = (
  dealershipDn?: string,
  fallbackCity?: string
) => {
  const [city, setCity] = useState(fallbackCity ?? "-");

  useEffect(() => {
    let isMounted = true;
    const dn = dealershipDn?.trim();
    const fallback = fallbackCity ?? "-";

    setCity(fallback);

    if (!dn) return;

    const cachedCity = dealershipCityCache.get(dn);
    if (cachedCity) {
      setCity(cachedCity);
      return;
    }

    getDealershipCityByDnCached(dn).then((resolvedCity) => {
      if (isMounted && resolvedCity) {
        setCity(resolvedCity);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [dealershipDn, fallbackCity]);

  return city;
};
